// 06 Retours · 07 Formation · 08 Tickets annulés — trois écrans jumeaux.
//
// Un seul composant : les trois pages V2 (RetoursClient, FormationClient,
// AnnulesClient) sont le même écran à trois détails près, et les avoir laissées
// diverger est précisément ce qui a fait que le filtre anomalies n'existe que
// sur les annulés. Ce qui diffère vraiment est isolé en trois endroits nommés :
// la source des lignes, le sous-titre d'une ligne, et la colonne « restants ».
// Les filtres, eux, sont désormais servis aux trois — le motif du commentaire
// ci-dessus était encore vrai dans le code d'hier.
//
// Conventions V2 tenues à la lettre (P8) :
//   • statut VALIDÉ = `null` dans <kind>Statuses — c'est ce qu'écrit le scan,
//     côté PC comme côté mobile (mobile_mark_ticket_validated) ;
//   • clé ABSENTE = jamais vu, ce qui n'est PAS « manquant » : un ticket jamais
//     présenté ne compte pas comme traité, et c'est ce qui empêche la lampe de
//     passer au vert sur un contrôle à moitié fait. Les deux ne se mélangent
//     nulle part : ils ont leur compteur, leur filtre et leur lampe ;
//   • set_ticket_status refuse un statut hors {valide, non_conforme, en_attente,
//     manquant} et refuse un ticket absent de l'import (ticket_not_found) ;
//     `p_status = NULL` écrit le marqueur JSON null — il n'EFFACE pas la clé,
//     donc on ne revient jamais à « jamais vu », et le sélecteur ne le propose
//     pas (recliquer l'état actif ne fait rien, contrairement à l'étape 05).
//
// Ajout V5, formation seulement : un motif LIBRE par ticket, obligatoire sur
// TOUT ticket porteur d'un statut (point ouvert O9) — c'est ce que compte
// `ticketsSansMotif`, et donc ce qui ferme ou non l'étape 07. Il voyage dans
// l'objet statut — { status, motif } — parce que `null` ne peut rien porter.
// set_ticket_status ne valide que la clé `status` et stocke le reste tel quel
// (vérifié dans 12_journee_typed_rpcs.sql) : rien à modifier côté RPC.
//
// Conséquence, sur la formation : les tickets SANS VALEUR ont eux aussi besoin
// d'un statut. La V2 les affichait en pastilles inertes ; ici ils reçoivent le
// même sélecteur, sinon `jamaisVus` ne tombe jamais à zéro et 07 reste ouverte.
// Et poser un statut y ouvre directement la modale du motif : le poser d'abord
// pour venir le compléter ensuite fabriquait un ticket bloquant en deux gestes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { etatTickets, ticketsSansMotif, type Kind } from '@/domain/derive'
import { couleurEcartUnites, fmtEcartUnites } from '@/domain/ecart'
import { useScanner } from '@/lib/scan'
import { fmtEur, heure, pluriel, todayIso } from '@/lib/format'
import { Card, Chip, Kpis, Lamp, Modale, Notice, Vide, type Couleur } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PropsEtape } from './types'

const KIND: Record<string, Kind> = { '07': 'retours', '08': 'formation', '09': 'annules' }
const NOM: Record<Kind, string> = { retours: 'retours', formation: 'formation', annules: 'annulés' }

/**
 * La lampe d'un écart exprimé en NOMBRE. `domain/ecart` donne la couleur CSS
 * (`couleurEcartUnites`) mais pas la Couleur d'une <Lamp> : le module est hors
 * limites ici, la conversion reste donc locale et suit exactement ses seuils.
 */
const lampeUnites = (n: number): Couleur => {
  const a = Math.abs(n)
  return a === 0 ? 'ok' : a === 1 ? 'warn' : 'stop'
}

/**
 * Les quatre états qu'un ticket peut porter. La V2 les servait dans un <select>
 * — deux clics, et les choix cachés tant qu'on n'a pas déroulé. Ils sont ici en
 * boutons `aria-pressed`, l'idiome du sélecteur de l'étape 05 : UN clic par
 * statut, et les quatre visibles en permanence.
 *
 * Le ton dit ce que le bouton fait : valider solde (ok), mettre en attente
 * diffère (warn), déclarer non conforme ou manquant est un refus / une bascule
 * en écart (stop). La valeur vide vaut VALIDÉ, donc `null` en base.
 */
const STATUTS: Array<{ v: string; court: string; libelle: string }> = [
  { v: '',             court: '✓', libelle: 'Validé' },
  { v: 'en_attente',   court: '⏳', libelle: 'En attente' },
  { v: 'non_conforme', court: '✗', libelle: 'Non conforme' },
  { v: 'manquant',     court: '∅', libelle: 'Manquant' },
]

/** Même échelle pour la lampe de la ligne, le ton du bouton et celui de la modale. */
const COULEUR_ETAT: Record<string, Couleur> = {
  '': 'ok', en_attente: 'warn', non_conforme: 'stop', manquant: 'stop',
}
const LIBELLE_ETAT: Record<string, string> = {
  '': 'validé', en_attente: 'en attente', non_conforme: 'non conforme', manquant: 'manquant',
}

type Filtre = 'tous' | 'jamais' | 'statue' | 'valeur'

// ── Lecture du blob ────────────────────────────────────────────────────────

const texte = (v: unknown): string => (v == null ? '' : String(v))
const objet = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
/** ORIKA colle le matricule derrière le nom : « DUPONT (1234) ». */
const nettoyer = (s: string): string => s.replace(/\s*\(\d+\)/, '').trim()

interface Ligne {
  ticket: string
  caisse: string
  operateur: string
  superviseur: string
  type: string
  libelle: string
  motif: string
  valeur: number
  hasValue: boolean
  /** Lignes ORIKA regroupées sous ce ticket — voir l'incident O10 plus bas. */
  occurrences: number
}

/**
 * Un ticket = une ligne à l'écran. Depuis juin 2026 ORIKA émet plusieurs lignes
 * par ticket annulé (une par article) : les afficher telles quelles ferait
 * compter deux fois le même ticket à contrôler. Les montants ne sont PAS
 * additionnés — on ne sait pas encore si la colonne porte le montant de
 * l'article ou celui du ticket (O10), et une somme fausse est pire qu'une somme
 * absente.
 */
function grouper(brut: unknown, kind: Kind): Ligne[] {
  const source = Array.isArray(brut) ? (brut as unknown[]) : []
  const par = new Map<string, Ligne>()
  for (const item of source) {
    const l = objet(item)
    const ticket = texte(l.ticket).trim()
    if (!ticket) continue
    const deja = par.get(ticket)
    if (deja) { deja.occurrences++; continue }
    const valeur = Number(l.valeur) || 0
    par.set(ticket, {
      ticket,
      caisse: texte(l.caisse).trim(),
      operateur: nettoyer(texte(l.operateur)),
      superviseur: nettoyer(texte(l.superviseur)),
      type: texte(l.type).trim(),
      libelle: texte(l.libelle).trim(),
      motif: texte(l.motif).trim(),
      valeur,
      // Le parseur pose hasValue=false quand la colonne montant contient un
      // Gencod (annulés, formation). Les retours n'ont pas cette clé et n'ont
      // pas ce problème : leur colonne montant est fiable, un retour à 0,00 €
      // reste un retour chiffré — l'inférer de `valeur !== 0` le rangeait à
      // tort parmi les montants inexploitables.
      hasValue: kind === 'retours' ? true
        : typeof l.hasValue === 'boolean' ? l.hasValue : valeur !== 0,
      occurrences: 1,
    })
  }
  return [...par.values()]
}

const orphelin = (ticket: string): Ligne => ({
  ticket, caisse: '', operateur: '', superviseur: '', type: '', libelle: '', motif: '',
  valeur: 0, hasValue: false, occurrences: 0,
})

interface Lecture {
  /** La clé existe dans la table des statuts — le ticket a été regardé. */
  vu: boolean
  valide: boolean
  /** '' quand validé (statut `null`), sinon le code du statut. */
  code: string
  note: string
  motif: string
  dateCreated: string
}

function lire(statuts: Record<string, unknown>, ticket: string): Lecture {
  const vu = Object.prototype.hasOwnProperty.call(statuts, ticket)
  const brut = vu ? statuts[ticket] : undefined
  const o = objet(brut)
  const code = texte(o.status)
  return {
    vu,
    // `null` = validé par scan (V2) ; 'valide' = validé avec motif (V5).
    valide: vu && (brut === null || code === 'valide'),
    code: brut === null ? '' : code,
    note: texte(o.note),
    motif: texte(o.motif),
    dateCreated: texte(o.dateCreated),
  }
}

/** Ce que set_ticket_status accepte : `status` validé, le reste stocké tel quel. */
interface Statut {
  status: string
  motif?: string
  note?: string
  dateCreated?: string
}

// ── L'écran ────────────────────────────────────────────────────────────────

export default function Tickets({ etape, rapport, lectureSeule }: PropsEtape) {
  const { date, ecrireRapport } = useRun()
  const toast = useToast()

  const kind = KIND[etape.code] ?? 'retours'
  const cle = `${kind}Statuses`

  const [saisie, setSaisie] = useState('')
  const [recherche, setRecherche] = useState('')
  const [filtre, setFiltre] = useState<Filtre>('tous')
  const [dernier, setDernier] = useState<
    { ticket: string; etat: 'ok' | 'dup' | 'err'; at: string; remplace?: string } | null
  >(null)
  /** `code` = le statut que la modale écrira ; '' = elle valide le ticket. */
  const [motifModale, setMotifModale] = useState<{ ticket: string; motif: string; code: string } | null>(null)
  /** Notes en cours de frappe : la V2 envoyait une RPC par caractère saisi. */
  const [notes, setNotes] = useState<Record<string, string>>({})
  /**
   * Le dernier motif réellement enregistré. Sur la formation, une série de
   * tickets partage presque toujours le même motif (« nouvelle caissière ») :
   * le retaper à chaque ticket est le vrai coût de cette étape. Il est proposé
   * en un clic, jamais pré-rempli — Entrée valide la modale, et un motif faux
   * posé par réflexe vaut moins que pas de motif du tout.
   */
  const [dernierMotif, setDernierMotif] = useState('')
  /** Le ticket dont l'écriture est en vol — rend ses commandes inertes (data-encours). */
  const [occupe, setOccupe] = useState<string | null>(null)
  const occupeRef = useRef<string | null>(null)

  const lignes = useMemo(() => grouper(rapport?.[kind], kind), [rapport, kind])
  const statuts = objet(rapport?.[cle])
  const e = etatTickets(rapport, kind)
  const sansMotif = kind === 'formation' ? ticketsSansMotif(rapport) : 0

  // Le rapport ne revient qu'au rendu SUIVANT : deux écritures parties dans le
  // même tour de boucle repartiraient toutes deux de la table d'avant, et la
  // seconde effacerait la première — c'est ce qui faisait disparaître une note
  // qu'on venait de taper quand on cliquait aussitôt sur un statut (le blur et
  // le clic tombent dans le même tour). La table de référence des écritures est
  // donc tenue dans une ref, avancée AVANT l'appel, et resynchronisée sur le
  // rapport à chaque rendu — y compris après une annulation, quand le provider
  // a remis l'état d'avant.
  const statutsRef = useRef<Record<string, unknown>>(statuts)
  useEffect(() => { statutsRef.current = statuts }, [statuts])

  const champScan = useRef<HTMLInputElement | null>(null)

  // ── Écriture — chemin unique, optimiste, annulée si la RPC refuse ────────

  const ecrire = useCallback(async (ticket: string, statut: Statut | null): Promise<boolean> => {
    const suivant = { ...statutsRef.current, [ticket]: statut }
    statutsRef.current = suivant
    setOccupe(ticket); occupeRef.current = ticket
    try {
      await ecrireRapport(
        `statut du ticket ${ticket}`,
        { [cle]: suivant },
        () => api.enregistrerStatutTicket(date, kind, ticket, statut),
        etape.code,
      )
      return true
    } catch {
      // Le provider a remis l'état d'avant et posé le message — dont la garde
      // ticket_not_found, traduite en « ce ticket n'est pas dans l'import ».
      return false
    } finally {
      setOccupe(null); occupeRef.current = null
    }
  }, [cle, date, ecrireRapport, etape.code, kind])

  /**
   * Poser un statut. Sur la formation, TOUT statut réclame son motif (O9) : on
   * ouvre la modale d'emblée plutôt que d'écrire un ticket qui bloquera l'étape
   * une seconde plus tard. Si le motif existe déjà, il vaut pour le nouveau
   * statut et l'écriture part directement — un clic, comme ailleurs.
   */
  const changerStatut = (ticket: string, valeur: string) => {
    const st = lire(statutsRef.current, ticket)
    if (kind === 'formation' && !st.motif) {
      setMotifModale({ ticket, motif: '', code: valeur })
      return
    }
    if (!valeur) {
      void ecrire(ticket, st.motif
        // Un validé porteur d'un motif ne peut pas s'écrire `null` : `null` ne
        // transporte rien, et le motif serait perdu au moment même où on valide.
        ? { status: 'valide', motif: st.motif, dateCreated: st.dateCreated || todayIso(),
            ...(st.note ? { note: st.note } : {}) }
        : null)
      return
    }
    void ecrire(ticket, {
      status: valeur,
      dateCreated: st.dateCreated || todayIso(),
      note: st.note,
      // Le motif déjà saisi survit à un changement de statut : la V2 le
      // recopiait par `{ ...status }`, l'écraser ferait perdre à l'opérateur
      // ce qu'il a écrit sans qu'aucun écran ne le dise.
      ...(st.motif ? { motif: st.motif } : {}),
    })
  }

  const poserNote = (ticket: string) => {
    const valeur = notes[ticket]
    const st = lire(statutsRef.current, ticket)
    if (valeur === undefined || valeur === st.note || !st.code) return
    // La frappe locale n'est relâchée qu'une fois la note en base : si la RPC
    // refuse, ce que l'opérateur a tapé reste à l'écran.
    void ecrire(ticket, {
      status: st.code,
      dateCreated: st.dateCreated || todayIso(),
      note: valeur,
      ...(st.motif ? { motif: st.motif } : {}),
    }).then((ok) => { if (ok) setNotes((n) => { const c = { ...n }; delete c[ticket]; return c }) })
  }

  const validerMotif = async () => {
    if (!motifModale || occupeRef.current) return
    const motif = motifModale.motif.trim()
    if (!motif) { toast('UN MOTIF EST OBLIGATOIRE SUR CE TICKET', true); return }
    const st = lire(statutsRef.current, motifModale.ticket)
    // La modale écrit le statut pour lequel elle a été ouverte : celui qu'on
    // vient de choisir, ou celui déjà en place quand on ne fait qu'ajouter le
    // motif. Elle ne transforme jamais un « non conforme » en validé au passage.
    const statut: Statut = {
      status: motifModale.code || 'valide',
      motif,
      dateCreated: st.dateCreated || todayIso(),
      ...(st.note ? { note: st.note } : {}),
    }
    // La modale ne se referme que si l'écriture est passée : sinon le motif
    // saisi serait perdu et le ticket resterait non validé sans qu'on le voie.
    if (!await ecrire(motifModale.ticket, statut)) return
    setDernierMotif(motif)
    if (!motifModale.code) {
      setDernier({ ticket: motifModale.ticket, etat: 'ok', at: new Date().toISOString() })
    }
    setMotifModale(null)
  }

  // ── Scan ────────────────────────────────────────────────────────────────

  const parTicket = useMemo(() => new Map(lignes.map((l) => [l.ticket, l])), [lignes])

  const traiter = useCallback(async (brut: string) => {
    if (lectureSeule) return
    const ticket = brut.trim().replace(/\D/g, '')
    if (!ticket) return
    // Le même ticket deux fois pendant que son écriture est en vol : la seconde
    // ne verrait pas encore le statut de la première et écrirait par-dessus.
    if (occupeRef.current === ticket) return
    const at = new Date().toISOString()
    const st = lire(statutsRef.current, ticket)
    // Doublon : le ticket est déjà validé. On le dit au lieu de réécrire.
    if (st.valide) { setDernier({ ticket, etat: 'dup', at }); return }
    const ligne = parTicket.get(ticket)
    if (!ligne) {
      setDernier({ ticket, etat: 'err', at })
      return
    }
    // Formation : le motif est obligatoire (O9) — mais s'il est DÉJÀ écrit, le
    // scan vaut validation et part directement. Rouvrir la modale pour faire
    // reconfirmer un motif qu'on vient de lire à l'écran est un geste pour
    // rien, et c'est déjà ce que fait le bouton « ✓ » de la ligne : le scan et
    // le bouton doivent valider le même ticket de la même façon.
    if (kind === 'formation') {
      if (!st.motif) { setMotifModale({ ticket, motif: '', code: '' }); return }
      const ok = await ecrire(ticket, {
        status: 'valide', motif: st.motif,
        dateCreated: st.dateCreated || todayIso(),
        ...(st.note ? { note: st.note } : {}),
      })
      if (ok) setDernier({ ticket, etat: 'ok', at, ...(st.code ? { remplace: st.code } : {}) })
      return
    }
    // Le bandeau n'annonce « Validé » qu'une fois l'écriture acceptée : la V2
    // l'affichait avant l'aller-retour, donc un refus laissait à l'écran la
    // confirmation d'un ticket qui n'avait pas été enregistré. Et il dit quand
    // le scan a effacé un statut posé à la main — « manquant » retrouvé au fond
    // d'un tiroir, c'est une information, pas un détail de mécanique.
    if (await ecrire(ticket, null)) {
      setDernier({ ticket, etat: 'ok', at, ...(st.code ? { remplace: st.code } : {}) })
    }
  }, [ecrire, kind, lectureSeule, parTicket])

  // La douchette pose un input caché qui reprend le focus : ouverte, la modale
  // du motif se ferait voler chaque frappe.
  useScanner(traiter, { minLength: 4, timeout: 80, actif: !lectureSeule && motifModale === null })

  // Le scan est l'action de l'écran : le champ visible garde le focus, au
  // montage comme au retour d'une modale. Déclaré APRÈS useScanner à dessein —
  // son effet vient de poser le focus sur l'input caché, celui-ci le reprend
  // aussitôt, et la douchette écrit alors là où l'opérateur la voit écrire.
  // L'input caché reste le filet quand le focus part ailleurs.
  const modaleOuverte = motifModale !== null
  useEffect(() => {
    if (lectureSeule || modaleOuverte) return
    champScan.current?.focus({ preventScroll: true })
  }, [lectureSeule, modaleOuverte])

  const scanner = () => {
    void traiter(saisie)
    setSaisie('')
    // Le clic a emporté le focus sur le bouton : on le rend au champ, sinon le
    // scan suivant tombe dans le vide.
    champScan.current?.focus({ preventScroll: true })
  }

  // ── Les deux colonnes ───────────────────────────────────────────────────

  // Les validés se lisent depuis les statuts, pas depuis l'import : un ticket
  // validé puis disparu d'un ré-import doit rester visible, sinon le travail
  // fait s'évapore de l'écran sans que personne le sache.
  //
  // L'ordre : les clés sont des nombres, et `Object.keys` les rend TRIÉES par
  // valeur croissante, jamais dans l'ordre où on les a posées — le `.reverse()`
  // d'hier promettait « le dernier scanné en tête » et donnait le plus grand
  // numéro. Le tri est donc explicitement numérique décroissant, et le ticket
  // qu'on vient de traiter est hissé en tête, lui pour de bon.
  const valides = useMemo(() => {
    const liste = Object.keys(statuts)
      .filter((t) => lire(statuts, t).valide)
      .map((t) => parTicket.get(t) ?? orphelin(t))
    liste.sort((a, b) => Number(b.ticket) - Number(a.ticket))
    return liste
  }, [statuts, parTicket])

  const enTete = dernier?.etat === 'ok' ? dernier.ticket : null
  const validesOrdonnes = useMemo(() => (enTete
    ? [...valides.filter((l) => l.ticket === enTete), ...valides.filter((l) => l.ticket !== enTete)]
    : valides), [valides, enTete])

  /** Validés qui ne sont plus dans l'import : leur statut est figé (ticket_not_found). */
  const orphelins = valides.filter((l) => l.occurrences === 0)
  const validesDansImport = valides.length - orphelins.length

  // Formation : seuls les tickets porteurs d'une valeur sont à retrouver (V2).
  const aControler = kind === 'formation' ? lignes.filter((l) => l.hasValue) : lignes
  const restants = aControler.filter((l) => !lire(statuts, l.ticket).valide)
  /** Ce qui BLOQUE dans cette colonne : jamais présenté ≠ statué non validé. */
  const jamaisVusIci = restants.filter((l) => !lire(statuts, l.ticket).vu).length

  const correspond = useCallback((l: Ligne, q: string): boolean => {
    if (!q) return true
    if (l.ticket.includes(q)) return true
    const bas = (s: string) => s.toLowerCase().includes(q)
    if (kind === 'retours')   return bas(l.operateur) || bas(l.libelle) || bas(l.motif)
    if (kind === 'formation') return bas(l.type) || bas(l.operateur)
    return bas(l.operateur) || bas(l.superviseur)
  }, [kind])

  // Les filtres sont servis aux trois écrans. « Avec valeur » n'a de sens que
  // là où la colonne est réellement mixte — l'offrir ailleurs serait un bouton
  // qui ne change rien.
  const mixteValeur = restants.some((l) => l.hasValue) && restants.some((l) => !l.hasValue)
  const filtres: Array<{ v: Filtre; l: string }> = [
    { v: 'tous', l: 'Tous' },
    { v: 'jamais', l: 'Jamais présentés' },
    { v: 'statue', l: 'Statués' },
    ...(mixteValeur ? [{ v: 'valeur' as Filtre, l: 'Avec valeur' }] : []),
  ]
  // Un filtre dont le bouton a disparu ne doit pas continuer à masquer des
  // lignes en silence.
  const filtreActif: Filtre = filtres.some((f) => f.v === filtre) ? filtre : 'tous'

  const q = recherche.toLowerCase().trim()
  const validesFiltres = validesOrdonnes.filter((l) => correspond(l, q))
  const restantsFiltres = restants.filter((l) => {
    const st = lire(statuts, l.ticket)
    if (filtreActif === 'valeur' && !l.hasValue) return false
    if (filtreActif === 'jamais' && st.vu) return false
    if (filtreActif === 'statue' && !st.vu) return false
    return correspond(l, q)
  })
  const filtrePose = filtreActif !== 'tous' || q !== ''

  // Les montants neutralisés valent 0 dans le parseur : les additionner ne
  // fausserait rien aujourd'hui, mais le total ne doit porter que ce qui est
  // chiffré — le jour où le Gencod passera (O10), la somme resterait juste.
  const chiffrees = lignes.filter((l) => l.hasValue)
  const valeurTotale = chiffrees.reduce((s, l) => s + l.valeur, 0)
  const avecValeur = chiffrees.length
  const sansValeur = lignes.length - avecValeur

  // Formation : un ticket sans montant n'est pas à RETROUVER — la V2 le sortait
  // des restants et on garde ce compte. Mais la règle de l'étape (derive.ts) les
  // compte dans `jamaisVus` : tant qu'aucun statut n'est posé dessus, 07 ne peut
  // pas se fermer. La V2 les affichait en pastilles inertes, donc l'étape était
  // structurellement infermable ici. Ils reçoivent le même sélecteur que les
  // autres, dans une liste à part qui dit pourquoi ils sont à part.
  const sansValeurLignes = lignes.filter((l) => !l.hasValue)
  const sansValeurAStatuer = sansValeurLignes.filter((l) => !lire(statuts, l.ticket).valide)
  const sansValeurJamaisVus = sansValeurLignes.filter((l) => !lire(statuts, l.ticket).vu).length

  const parOperateur = useMemo(() => {
    const groupes = new Map<string, number>()
    for (const l of lignes) {
      if (!l.hasValue) continue
      const op = l.operateur || '—'
      groupes.set(op, (groupes.get(op) ?? 0) + l.valeur)
    }
    return [...groupes.entries()].sort((a, b) => b[1] - a[1])
  }, [lignes])

  // ── Absences ────────────────────────────────────────────────────────────

  if (kind !== 'retours' && rapport?.operationsLoaded !== true) {
    return (
      <Notice c="warn">
        <b>Fichier « Opérations à risque » non importé</b> — les tickets {NOM[kind]} en viennent.
        Tant qu’il manque, cette étape n’a rien à contrôler : l’import se fait à l’étape 01.
      </Notice>
    )
  }

  if (lignes.length === 0) {
    return (
      <Vide>
        Aucun ticket {NOM[kind]} dans l’import du jour — cette étape aurait dû être grisée
        par règle. Vérifie l’étape 01 si le fichier a bien été chargé.
      </Vide>
    )
  }

  // La condition EXACTE de la règle de l'étape (derive.ts, cas 06/07/08) : un
  // ticket déclaré manquant ou non conforme est un ticket TRAITÉ, il ne bloque
  // rien. Y ajouter « aucun restant » — ce que faisait la lampe d'hier — laissait
  // l'écran à l'ambre pendant que le rail annonçait l'étape prête.
  const toutStatue = e.jamaisVus === 0 && sansMotif === 0

  // ── Rendu ───────────────────────────────────────────────────────────────

  return (
    <>
      {kind === 'annules' && (
        <Notice c="warn">
          <b>Incident connu, non corrigé (point ouvert O10)</b> — depuis juin 2026, ORIKA émet
          une ligne par article et place le Gencod dans la colonne montant. Le parseur neutralise
          ces montants plutôt que d’afficher un faux euro : {sansValeur > 0
            ? `${pluriel(sansValeur, 'ticket arrive', 'tickets arrivent')} sans montant exploitable`
            : 'aucun ticket n’est concerné aujourd’hui'}. Les lignes d’un même ticket sont
          regroupées mais leurs montants ne sont PAS additionnés, tant qu’on ne sait pas si la
          colonne porte le montant de l’article ou celui du ticket. Le canary du parseur
          (comparaison au nombre annoncé par le fichier) reste actif à l’étape 01.
        </Notice>
      )}

      {kind === 'formation' && sansMotif > 0 && (
        <Notice c="warn">
          <b>{pluriel(sansMotif, 'ticket statué', 'tickets statués')} sans motif</b> — le motif est
          la seule information que cette étape produit, et la règle de l’étape 07 compte TOUT
          ticket porteur d’un statut, pas seulement les validés : un ticket marqué « non conforme »
          ou « manquant » en réclame un aussi. Le bouton « Saisir le motif » est sur chaque ligne
          concernée, dans les listes ci-dessous. Tant qu’il en reste, l’étape ne peut pas se fermer.
        </Notice>
      )}

      {orphelins.length > 0 && (
        <Notice c="warn">
          <b>{fmtEcartUnites(orphelins.length, 'ticket validé hors import', 'tickets validés hors import')}</b>
          {' '}— ils portent un statut mais ne sont plus dans le fichier du jour, sans doute après un
          ré-import. Le travail fait reste affiché, mais leur statut est figé : la RPC refuse
          d’écrire sur un ticket absent de l’import (<span className="mo">ticket_not_found</span>).
          Ils ne bloquent pas l’étape ; pour les rouvrir, il faut réimporter le fichier qui les porte.
        </Notice>
      )}

      <Kpis items={[
        {
          k: 'Tickets', v: e.total,
          s: kind === 'retours' ? undefined : `${avecValeur} avec valeur`,
        },
        {
          k: 'Validés', v: validesDansImport, s: `sur ${e.total}`,
          // Le vert dit « c'est fait », pas « c'est commencé » : trois validés
          // sur vingt ne méritent pas la même couleur que vingt sur vingt. Il ne
          // dit pas non plus « c'est fermable » : l'étape se clôt avec des
          // tickets déclarés manquants, mais ce KPI compte les VALIDÉS — le
          // teindre en vert à côté d'un « Déclarés manquants » rouge ferait dire
          // au vert qu'un écart non nul est conforme. Vert au seul 20 sur 20.
          couleur: e.total > 0 && validesDansImport === e.total ? 'var(--ok)' : undefined,
        },
        {
          k: 'Jamais présentés', v: e.jamaisVus,
          s: e.jamaisVus > 0 ? 'bloquent l’étape' : 'aucun',
          couleur: couleurEcartUnites(e.jamaisVus),
        },
        { k: 'Anomalies', v: e.anomalies, couleur: couleurEcartUnites(e.anomalies) },
        { k: 'Déclarés manquants', v: e.manquants, couleur: couleurEcartUnites(e.manquants) },
        {
          k: 'Valeur', v: fmtEur(valeurTotale),
          s: sansValeur > 0 ? `${avecValeur} tickets chiffrés` : undefined,
        },
      ]} />

      {kind === 'retours' && parOperateur.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {parOperateur.slice(0, 4).map(([op, total]) => (
            <Chip key={op}>{op} · {fmtEur(total)}</Chip>
          ))}
        </div>
      )}

      {dernier && (
        <Notice
          c={dernier.etat === 'ok' ? 'ok' : dernier.etat === 'err' ? 'stop' : 'warn'}
          action={<button className="btn gh sm" onClick={() => setDernier(null)}>Fermer</button>}
        >
          <b className="mo">#{dernier.ticket}</b> —{' '}
          {dernier.etat === 'ok' ? 'Validé'
            : dernier.etat === 'err' ? 'Ticket introuvable dans l’import du jour'
            : 'Déjà validé — rien n’a été réécrit'} · {heure(dernier.at)}
          {dernier.remplace && (
            <> · le statut « {LIBELLE_ETAT[dernier.remplace] ?? dernier.remplace} » a été remplacé</>
          )}
        </Notice>
      )}

      <Card
        titre="Contrôle des tickets"
        lampe={toutStatue ? 'ok' : 'warn'}
        actions={lectureSeule
          ? <Chip>LECTURE SEULE</Chip>
          : <Chip c="act">DOUCHETTE HID · MIN 4 · 80 MS</Chip>}
      >
        <div style={{ display: 'flex', gap: 8, padding: '10px 12px', flexWrap: 'wrap' }}>
          <input
            ref={champScan}
            className="fld mo" style={{ flex: 1, minWidth: 200 }} value={saisie}
            disabled={lectureSeule}
            aria-label="Numéro de ticket à contrôler"
            placeholder="Numéro de ticket (douchette ou saisie manuelle)…"
            onChange={(ev) => setSaisie(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') scanner() }}
          />
          <button
            className="btn pri lg" data-encours={occupe ? 'true' : undefined}
            disabled={lectureSeule || !saisie.trim()} onClick={scanner}
          >
            {/* Le bouton dit ce qu'il fait : sur la formation, il n'enregistre
                rien tout seul — il ouvre la saisie du motif, sans lequel aucun
                ticket ne peut être validé (O9). */}
            {kind === 'formation' ? 'Valider avec un motif' : 'Valider le ticket'}
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 8, padding: '0 12px 10px', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <input
            className="fld" style={{ flex: 1, minWidth: 180 }} value={recherche}
            aria-label="Rechercher dans tous les tickets"
            placeholder="Rechercher dans tous les tickets…"
            onChange={(ev) => setRecherche(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Escape') setRecherche('') }}
          />
          {recherche && (
            <button className="btn gh sm" onClick={() => setRecherche('')}>Effacer la recherche</button>
          )}
          {/* Sélecteur d'état : le choix retenu est dit par aria-pressed, que le
              CSS stylise seul — pas besoin d'un ton en plus. */}
          {filtres.map(({ v, l }) => (
            <button
              key={v} className="btn sm" aria-pressed={filtreActif === v}
              onClick={() => setFiltre(v)}
            >{l}</button>
          ))}
        </div>
      </Card>

      <div className="bl-grid" style={{ marginTop: 12 }}>
        <Card
          titre={`Validés — ${valides.length}`}
          lampe={valides.length > 0 ? 'ok' : 'neutre'}
          actions={validesFiltres.length !== valides.length
            ? <Chip>{validesFiltres.length} AFFICHÉS</Chip> : undefined}
        >
          {validesFiltres.length === 0
            ? <Vide>{valides.length === 0
                ? 'Aucun ticket validé — scanne le premier dans le champ ci-dessus.'
                : 'Aucun validé ne correspond à la recherche.'}</Vide>
            // La colonne des validés grossit toute la matinée : sans plafond,
            // elle pousse hors de l'écran la seule colonne où il reste à
            // travailler. Même hauteur que les restants, les deux restent en vis-à-vis.
            : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {validesFiltres.map((l) => (
                  <Rangee
                    key={l.ticket} l={l} st={lire(statuts, l.ticket)} kind={kind}
                    lectureSeule={lectureSeule} note={notes[l.ticket]} occupe={occupe === l.ticket}
                    changerStatut={changerStatut}
                    changerNote={(t, v) => setNotes((n) => ({ ...n, [t]: v }))}
                    poserNote={poserNote}
                    ouvrirMotif={(t, m, c) => setMotifModale({ ticket: t, motif: m, code: c })}
                  />
                ))}
              </div>
            )}
        </Card>

        <Card
          titre={`Restants — ${restants.length}`}
          // La lampe suit ce qui bloque : des tickets déclarés manquants laissent
          // la colonne pleine sans empêcher l'étape de se fermer.
          lampe={lampeUnites(jamaisVusIci)}
          actions={<>
            {jamaisVusIci > 0 && <Chip c="warn">{jamaisVusIci} JAMAIS PRÉSENTÉS</Chip>}
            {restantsFiltres.length !== restants.length && (
              <Chip>{restantsFiltres.length} AFFICHÉS</Chip>
            )}
          </>}
        >
          {restants.length === 0 ? (
            <div className="li">
              <Lamp c={toutStatue ? 'ok' : 'warn'} />
              <div className="body">
                <div className="t">
                  {/* Un état vide n'est pas un état conforme : la colonne peut être
                      vidée alors que l'étape reste bloquée ailleurs. On dit où. */}
                  {sansValeurJamaisVus > 0
                    ? `Plus rien à retrouver ici — ${pluriel(sansValeurJamaisVus, 'ticket sans valeur attend', 'tickets sans valeur attendent')} encore un statut, plus bas.`
                    : sansMotif > 0
                      ? `Plus rien à retrouver ici — ${pluriel(sansMotif, 'ticket attend', 'tickets attendent')} son motif.`
                      : 'Tous les tickets sont validés.'}
                </div>
              </div>
            </div>
          ) : restantsFiltres.length === 0 ? (
            <Vide>
              Aucun restant ne correspond au filtre.{' '}
              {filtrePose && (
                <button className="btn gh sm" style={{ marginTop: 8 }}
                        onClick={() => { setFiltre('tous'); setRecherche('') }}>
                  Voir les {restants.length} restants
                </button>
              )}
            </Vide>
          ) : (
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              {restantsFiltres.map((l) => (
                <Rangee
                  key={l.ticket} l={l} st={lire(statuts, l.ticket)} kind={kind}
                  lectureSeule={lectureSeule} note={notes[l.ticket]} occupe={occupe === l.ticket}
                  changerStatut={changerStatut}
                  changerNote={(t, v) => setNotes((n) => ({ ...n, [t]: v }))}
                  poserNote={poserNote}
                  ouvrirMotif={(t, m, c) => setMotifModale({ ticket: t, motif: m, code: c })}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {kind === 'formation' && sansValeur > 0 && (
        <Card
          titre={`Tickets sans valeur — ${sansValeur}`}
          // Ce qui compte ici est d'avoir REGARDÉ chaque ticket (la règle de
          // l'étape parle de `jamaisVus`), pas de les avoir tous validés.
          lampe={lampeUnites(sansValeurJamaisVus)}
          style={{ marginTop: 12 }}
          actions={sansValeurAStatuer.length !== sansValeur
            ? <Chip c="ok">{sansValeur - sansValeurAStatuer.length} VALIDÉS</Chip> : undefined}
        >
          <div className="hint" style={{ padding: '10px 12px 4px' }}>
            Ces tickets n’ont aucun montant : ils ne sont pas comptés dans les restants et il n’y a
            rien à retrouver dessus. Ils réclament quand même un statut — la règle de l’étape
            compte tout ticket de l’import, et un ticket jamais regardé la bloque.
            Les validés sont passés dans la colonne « Validés ».
          </div>
          {sansValeurAStatuer.length === 0
            ? <Vide>Tous les tickets sans valeur ont été validés.</Vide>
            : sansValeurAStatuer.map((l) => (
                <Rangee
                  key={l.ticket} l={l} st={lire(statuts, l.ticket)} kind={kind}
                  lectureSeule={lectureSeule} note={notes[l.ticket]} occupe={occupe === l.ticket}
                  changerStatut={changerStatut}
                  changerNote={(t, v) => setNotes((n) => ({ ...n, [t]: v }))}
                  poserNote={poserNote}
                  ouvrirMotif={(t, m, c) => setMotifModale({ ticket: t, motif: m, code: c })}
                />
              ))}
        </Card>
      )}

      {motifModale && (
        <Modale
          titre="Motif de la formation"
          sous={`Ticket #${motifModale.ticket} — le motif est obligatoire pour poser « ${LIBELLE_ETAT[motifModale.code] ?? motifModale.code} » sur ce ticket. Il est libre : écris ce qui s'est réellement passé, pas un code.`}
          fermer={() => setMotifModale(null)}
        >
          <label className="lbl" htmlFor="motif-ticket">Motif</label>
          <input
            id="motif-ticket" value={motifModale.motif} autoFocus
            placeholder="Ex. : nouvelle caissière, essai de la remise fidélité"
            onChange={(ev) => setMotifModale((m) => (m ? { ...m, motif: ev.target.value } : m))}
            onKeyDown={(ev) => { if (ev.key === 'Enter') void validerMotif() }}
          />
          {/* Une série de tickets de formation partage presque toujours le même
              motif : le reproposer en un clic évite de le retaper vingt fois.
              Proposé, jamais pré-rempli — Entrée valide, et un motif posé par
              réflexe vaut moins que pas de motif. */}
          {dernierMotif && dernierMotif !== motifModale.motif && (
            <button
              className="btn gh sm" style={{ marginTop: 6, maxWidth: '100%' }}
              title={dernierMotif}
              onClick={() => setMotifModale((m) => (m ? { ...m, motif: dernierMotif } : m))}
            >
              Reprendre «&nbsp;{dernierMotif.length > 42
                ? dernierMotif.slice(0, 42) + '…' : dernierMotif}&nbsp;»
            </button>
          )}
          <div className="hint" style={{ marginBottom: 10, marginTop: 6 }}>
            Il est enregistré avec le statut du ticket et ressort au bilan. Entrée valide, Échap annule.
          </div>
          <div className="btns">
            <button className="btn" onClick={() => setMotifModale(null)}>Annuler</button>
            <button
              // Le ton dit l'issue : valider solde, mettre en attente diffère,
              // déclarer non conforme ou manquant refuse.
              className={`btn ${COULEUR_ETAT[motifModale.code] ?? 'ok'}`}
              data-encours={occupe ? 'true' : undefined}
              disabled={!motifModale.motif.trim()}
              onClick={() => void validerMotif()}
            >
              {motifModale.code
                ? `Marquer « ${LIBELLE_ETAT[motifModale.code] ?? motifModale.code} »`
                : 'Valider le ticket'}
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

// ── Une ligne de ticket ────────────────────────────────────────────────────

function Rangee({ l, st, kind, lectureSeule, note, occupe, changerStatut, changerNote, poserNote, ouvrirMotif }: {
  l: Ligne
  st: Lecture
  kind: Kind
  lectureSeule: boolean
  note: string | undefined
  /** Une écriture est en vol sur ce ticket : ses commandes sont inertes. */
  occupe: boolean
  changerStatut: (ticket: string, valeur: string) => void
  changerNote: (ticket: string, valeur: string) => void
  poserNote: (ticket: string) => void
  /** `code` = le statut que la modale écrira ; '' = elle valide le ticket. */
  ouvrirMotif: (ticket: string, motif: string, code: string) => void
}) {
  // La V2 ouvre un champ « Note… » sur les statuts qui ne sont ni validé ni
  // manquant : ce sont ceux qui appellent une explication.
  const anomalie = st.vu && !st.valide && st.code !== '' && st.code !== 'manquant'
  const lampe: Couleur = !st.vu ? 'neutre' : st.valide ? 'ok' : (COULEUR_ETAT[st.code] ?? 'warn')

  // Un ticket absent de l'import ne peut plus rien recevoir : set_ticket_status
  // lève ticket_not_found. Lui laisser un sélecteur, c'était promettre une
  // écriture qui échouait — et, sur la formation, une modale du motif dont on ne
  // pouvait plus sortir autrement qu'en annulant.
  const horsImport = l.occurrences === 0
  const fige = lectureSeule || horsImport

  const bouts = kind === 'retours'
    ? [l.operateur, l.libelle, l.motif]
    : kind === 'formation'
      ? [l.type, l.operateur]
      : [l.operateur, l.superviseur, l.caisse && `CAISSE ${l.caisse}`]
  const sous = bouts.filter(Boolean).join(' · ')

  return (
    <div className="li">
      <Lamp c={lampe} />
      <div className="body">
        <div className="t">
          <span className="mo" style={{ color: st.valide ? 'var(--ok)' : 'var(--ink)' }}>
            #{l.ticket}
          </span>
          {l.occurrences > 1 && (
            <span style={{ marginLeft: 8 }}>
              <Chip c="warn">{l.occurrences} LIGNES ORIKA</Chip>
            </span>
          )}
          {horsImport && (
            <span style={{ marginLeft: 8 }}><Chip c="warn">HORS IMPORT</Chip></span>
          )}
        </div>
        {sous && <div className="s">{sous.toUpperCase()}</div>}
        {!st.vu && <div className="s">JAMAIS PRÉSENTÉ — NON TRAITÉ</div>}
        {st.motif && <div className="s">MOTIF : {st.motif.toUpperCase()}</div>}
        {anomalie && (fige ? (
          st.note && <div className="s">NOTE : {st.note.toUpperCase()}</div>
        ) : (
          <input
            className="fld" style={{ marginTop: 6, fontSize: 11.5 }} placeholder="Note…"
            aria-label={`Note du ticket ${l.ticket}`}
            value={note ?? st.note}
            onChange={(ev) => changerNote(l.ticket, ev.target.value)}
            onBlur={() => poserNote(l.ticket)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') ev.currentTarget.blur() }}
          />
        ))}
      </div>

      {/* Les montants se lisent en colonne, à droite, en chasse fixe : c'est
          ainsi qu'on repère un chiffre aberrant sans le chercher. */}
      <span
        className="mo"
        style={{
          minWidth: 74, textAlign: 'right', fontSize: 12,
          color: l.hasValue ? 'var(--ink2)' : 'var(--ink4)',
        }}
        title={l.hasValue ? undefined : 'Montant non exploitable — Gencod dans la colonne (O10)'}
      >
        {l.hasValue ? fmtEur(l.valeur) : '—'}
      </span>

      {fige ? (
        st.valide ? <Chip c="ok">VALIDÉ</Chip>
          : st.code === 'manquant' ? <Chip c="stop">MANQUANT</Chip>
          : st.code ? <Chip c="warn">{st.code.replace('_', ' ').toUpperCase()}</Chip>
          : <Chip>JAMAIS VU</Chip>
      ) : (
        <>
          {/* Tout ticket de formation PORTEUR D'UN STATUT réclame un motif, pas
              seulement les validés : c'est ce que compte `ticketsSansMotif`, et
              donc ce qui bloque l'étape. Le bouton ouvre la même modale en
              conservant le statut en place — un motif ne valide pas un ticket. */}
          {kind === 'formation' && st.vu && (
            <button
              className={`btn${st.motif ? ' gh' : ''} sm`}
              data-encours={occupe ? 'true' : undefined}
              onClick={() => ouvrirMotif(l.ticket, st.motif, st.valide ? '' : st.code)}
            >
              {st.motif ? 'Modifier le motif' : 'Saisir le motif'}
            </button>
          )}
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}
               role="group" aria-label={`Statut du ticket ${l.ticket}`}>
            {STATUTS.map(({ v, court, libelle }) => {
              // Validé s'écrit `null` (scan) ou { status: 'valide' } (motif V5) :
              // les deux allument le même bouton, celui de la valeur vide.
              const actif = st.vu && (v === '' ? st.valide : st.code === v)
              // Le ton n'est porté QUE par le bouton actif : `.btn.ok[aria-pressed]`
              // le remplit en plein, comme partout ailleurs dans la V3. Quatre
              // boutons tonés par ligne sur quarante lignes, c'est l'écran couvert
              // d'aplats que le CSS dit vouloir éviter — et on ne repère plus d'un
              // coup d'œil le statut posé. Un seul bouton plein par ligne, donc.
              // Les couleurs d'hier étaient en style inline : elles écrasaient à la
              // fois le remplissage au survol et celui de l'état appuyé, et
              // rendaient le statut actif en lavis pâle au lieu du plein du système.
              const ton = COULEUR_ETAT[v]
              return (
                <button
                  key={v || 'valide'} title={libelle} aria-label={libelle}
                  className={`btn${actif && ton ? ` ${ton}` : ''} sm`}
                  aria-pressed={actif} data-encours={occupe ? 'true' : undefined}
                  // Recliquer l'état actif ne fait rien : contrairement à
                  // l'étape 05, il n'y a pas de retour en arrière — la RPC écrit
                  // le marqueur JSON null au lieu d'effacer la clé, donc
                  // « jamais vu » n'est plus atteignable une fois quitté.
                  onClick={() => { if (!actif) changerStatut(l.ticket, v) }}
                  style={{ width: 28, height: 24, padding: 0, lineHeight: 1 }}
                >{court}</button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
